import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Button } from '../components/button/button.index';
import { Input } from '../components/input/input.index';
import { CiSearch } from 'react-icons/ci';
import { ColumnType } from 'antd/lib/table';
import { Table } from '../components/table/table.index';
import { BsFillInfoSquareFill } from 'react-icons/bs';
import { Badge } from '../components/badge/badge.index';
import { ConfigProvider, Divider, Pagination as AntPagination, Spin } from 'antd';
import { ReportDetailModal } from './reportDetailModal/reportDetailModal.index';
import CoinIcon from '../../utilities/components/coinIcon/coinIcon.index';
import { ReactComponent as InfoIcon } from 'assets/icons/Info.svg';
import { Enum, OrderTransaction, Pagination, Transaction } from '../../interfaces';
import {
    getOrderTransactionStates,
    getOrderTransactionTypes,
    getTransactionStates,
    getTransactionTypes,
} from 'services';
import { getOrderTransactions, getTransactions } from './reports.service';
import fa_IR from 'antd/locale/fa_IR';
import { getOrderTransactionBadgeType, getTransactionBadgeType, getTransactionTypeBadgeType } from '../../scripts';
import moment from 'jalali-moment';
import { __ReportsProps, __ReportsRef } from './reports.interface';
export enum ReportType {
    OrderTransaction,
    Transaction,
}

const reportTypes = [
    { id: 0, title: 'خرید/فروش', reportType: ReportType.OrderTransaction },
    { id: 1, title: 'واریز/برداشت', reportType: ReportType.Transaction },
];
export interface ReportsRef extends __ReportsRef {}
const Reports: React.ForwardRefRenderFunction<__ReportsRef, __ReportsProps> = (props: __ReportsProps, ref) => {
    useImperativeHandle(ref, () => ({
        updateData: () => {
            fetchData(page, pageSize);
        },
    }));

    const [loading, setLoading] = useState<boolean>(false);
    const [selectedReportType, setSelectedReportType] = useState<ReportType>(props.type ?? ReportType.OrderTransaction);

    const [orderTransactions, setOrderTransactions] = useState<Pagination<OrderTransaction>>();
    const [transactions, setTransactions] = useState<Pagination<Transaction>>();

    const [selectedOrderTransaction, setSelectedOrderTransaction] = useState<OrderTransaction>();
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction>();

    const [orderTransactionStates, setOrderTransactionStates] = useState<Enum[]>();
    const [orderTransactionTypes, setOrderTransactionTypes] = useState<Enum[]>();

    const [transactionStates, setTransactionStates] = useState<Enum[]>();
    const [transactionTypes, setTransactionTypes] = useState<Enum[]>();

    const [selectedOrderTransactionState, setSelectedOrderTransactionState] = useState<Enum | null>(null);
    const [selectedOrderTransactionType, setSelectedOrderTransactionType] = useState<Enum | null>(null);

    const [selectedTransactionState, setSelectedTransactionState] = useState<Enum | null>(null);
    const [selectedTransactionType, setSelectedTransactionType] = useState<Enum | null>(null);

    const [filtersLoading, setFiltersLoading] = useState<boolean>(true);
    const [keyword, setKeyword] = useState<string>('');
    const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false);

    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);

    const orderTransactionColumns: ColumnType<any>[] = [
        {
            title: 'ردیف',
            dataIndex: 'id',
            key: 'id',
            render: (text, record: OrderTransaction, idx) => <span className={'font-medium text-sm'}>{idx + 1}</span>,
        },
        {
            title: 'ارز مقصد / ارز مبدا',
            dataIndex: 'pair',
            align: 'center',
            key: 'pair',
            render: (pair, record: OrderTransaction) => (
                <div className={'flex items-center justify-center gap-3 en'}>
                    <CoinIcon symbol={record.sourceSymbol} className={'w-8 h-8'} /> {record.sourceSymbol} /{' '}
                    {record.destinationSymbol} <CoinIcon symbol={record.destinationSymbol} className={'w-8 h-8'} />
                </div>
            ),
        },
        {
            title: 'مقدار ارز مبدا',
            dataIndex: 'sourceAmount',
            key: 'sourceAmount',
            render: (text, record: OrderTransaction) => (
                <span className={'font-medium text-sm'}>{record.sourceAmount.toLocaleString()}</span>
            ),
        },
        {
            title: 'مقدار ارز مقصد',
            dataIndex: 'destinationAmount',
            key: 'destinationAmount',
            render: (text, record: OrderTransaction) => <span>{record.destinationAmount.toLocaleString()}</span>,
        },
        {
            title: 'نوع تراکنش',
            dataIndex: 'orderTransactionType',
            key: 'orderTransactionType',
            render: (text, record: OrderTransaction) =>
                record.orderTransactionType && (
                    <Badge
                        noIcon
                        type={record.orderTransactionType.id === 1 ? 'success' : 'danger'}
                        title={record.orderTransactionType.title}
                    />
                ),
        },
        {
            title: 'زمان',
            dataIndex: 'createdOn',
            key: 'createdOn',
            render: (text, record: OrderTransaction) => (
                <div className={'flex items-center gap-1'}>
                    {new Date(record.createdOn).toLocaleDateString('fa-IR', { timeZone: 'UTC' })}
                    <span className={'font-medium text-xs text-black-60'}>
                        {moment.utc(record.createdOn).locale('fa').local().local().format('HH:mm')}
                    </span>
                </div>
            ),
        },
        {
            title: 'وضعیت',
            dataIndex: 'orderTransactionState',
            key: 'orderTransactionState',
            render: (text, record: OrderTransaction) =>
                record.orderTransactionState && (
                    <div>
                        <Badge
                            type={
                                getOrderTransactionBadgeType(record.orderTransactionState.id) as
                                    | 'success'
                                    | 'warning'
                                    | 'danger'
                                    | 'info'
                            }
                            title={record.orderTransactionState.title}
                        />
                    </div>
                ),
            align: 'center',
        },
        {
            title: 'جزئیات',
            dataIndex: 'details',
            key: 'details',
            render: (text, record) => (
                <InfoIcon className={'clickable'} onClick={() => handleOrderTransactionDetail(record)} />
            ),
        },
    ];

    const transactionColumns: ColumnType<any>[] = [
        {
            title: 'ردیف',
            dataIndex: 'id',
            key: 'id',
            render: (text, record: Transaction, idx) => <span className={'font-medium text-sm'}>{idx + 1}</span>,
        },
        {
            title: 'ارز',
            dataIndex: 'market',
            key: 'market',
            render: (pair, record: Transaction) => (
                <div className={'flex items-center gap-3'}>
                    <CoinIcon symbol={record?.market.symbol} className={'w-8 h-8'} />
                    <span className={'text-sm font-bold'}>{record?.market.name}</span>
                    <span className={'text-xs font-medium en'}>{record?.market?.symbol?.toUpperCase()}</span>
                </div>
            ),
        },
        {
            title: 'مقدار ارز',
            dataIndex: 'amount',
            key: 'amount',
            render: (text, record: Transaction) => (
                <span className={'font-medium text-sm flex items-center gap-1'}>
                    <p>{record.amount.toLocaleString()}</p>
                    <p className="en">{record.market.symbol}</p>
                </span>
            ),
        },
        // {
        //     title: 'قیمت واحد',
        //     dataIndex: 'onPrice',
        //     key: 'onPrice',
        //     render: (text, record: Transaction) => (
        //         <span className={'font-medium text-sm'}>{record.onPrice.toLocaleString()} تومان</span>
        //     ),
        // },
        {
            title: 'نوع تراکنش',
            dataIndex: 'type',
            key: 'type',
            render: (text, record: Transaction) =>
                record.type && (
                    <Badge
                        noIcon
                        type={
                            getTransactionTypeBadgeType(record.type.id) as
                                | 'success'
                                | 'warning'
                                | 'danger'
                                | 'info'
                                | undefined
                        }
                        title={record.type.title}
                    />
                ),
        },
        {
            title: 'زمان',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text, record: Transaction) => (
                <div className={'flex items-center gap-1'}>
                    <span className={'font-medium text-sm'}>{new Date(record.createdAt).toLocaleDateString('fa')}</span>
                    <span className={'font-medium text-xs text-black-60'}>
                        {new Date(record.createdAt).toLocaleTimeString('fa')}
                    </span>
                </div>
            ),
        },
        {
            title: 'وضعیت',
            dataIndex: 'state',
            key: 'state',
            render: (text, record: Transaction) =>
                record.state && (
                    <Badge
                        type={getTransactionBadgeType(record.state.id) as 'success' | 'warning' | 'danger' | 'info'}
                        title={record.state.title}
                    />
                ),
            align: 'center',
        },
        {
            title: 'جزئیات',
            dataIndex: 'details',
            key: 'details',
            render: (text, record: Transaction) => (
                <InfoIcon className={'clickable'} onClick={() => handleTransactionDetail(record)} />
            ),
        },
    ];

    const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setKeyword(event.target.value);
    };

    const handleOrderTransactionDetail = (item: OrderTransaction) => {
        setSelectedOrderTransaction(item);
        setDetailModalOpen(true);
    };

    const handleTransactionDetail = (item: Transaction) => {
        setSelectedTransaction(item);
        setDetailModalOpen(true);
    };

    const buildQueryString = () => {
        const params = new URLSearchParams();
        if (selectedReportType === ReportType.OrderTransaction) {
            if (selectedOrderTransactionState) {
                params.append('state', selectedOrderTransactionState.id.toString());
            }
            if (selectedOrderTransactionType) {
                params.append('type', selectedOrderTransactionType.id.toString());
            }
        } else {
            if (selectedTransactionState) {
                params.append('state', selectedTransactionState.id.toString());
            }
            if (selectedTransactionType) {
                params.append('type', selectedTransactionType.id.toString());
            }
        }
        return params.toString();
    };

    const fetchData = (page: number, pageSize: number) => {
        const queryString = buildQueryString();

        const finalQueryString = queryString ? `?${queryString}` : '';

        if (selectedReportType === ReportType.OrderTransaction) {
            getOrderTransactions(setLoading, setOrderTransactions, page, pageSize, finalQueryString);
        } else {
            getTransactions(setLoading, setTransactions, page, pageSize, finalQueryString);
        }
    };

    const handlePageChange = (page: number, pageSize: number) => {
        setPage(page);
        setPageSize(pageSize);
    };

    function renderOrderTransactionItem(item: OrderTransaction) {
        return (
            <li className="p-4 rounded-[8px] bg-white border border-primary-20" key={item.id}>
                <div className="flex items-center justify-between">
                    <div className={'flex items-center gap-2'}>
                        <CoinIcon symbol={item.sourceSymbol} className="w-8 h-8" />
                        <span className={'text-xs font-medium en'}>{item.sourceSymbol}</span> /
                        <span className={'text-xs font-medium en'}>{item.destinationSymbol}</span>
                        <CoinIcon symbol={item.destinationSymbol} className="w-8 h-8" />
                    </div>
                    {item.orderTransactionState && (
                        <Badge
                            title={item.orderTransactionState.title}
                            type={
                                getOrderTransactionBadgeType(item.orderTransactionState.id) as
                                    | 'success'
                                    | 'warning'
                                    | 'danger'
                                    | 'info'
                            }
                        />
                    )}
                </div>
                <Divider className={'my-3'} />
                <div className="flex flex-col gap-2">
                    <div className={'flex justify-between'}>
                        <p className={'flex flex-col'}>
                            <strong className={'font-bold text-sm text-darkPurple-100'}>مقدار ارز مبدا:</strong>
                            <span className={'flex items-center gap-1 font-medium text-sm text-darkPurple-60'}>
                                {item.sourceAmount.toLocaleString()}
                                <span className={'en'}>{item.sourceSymbol}</span>
                            </span>
                        </p>
                        <p className={'flex flex-col'}>
                            <strong className={'font-bold text-sm text-darkPurple-100'}>مقدار ارز مقصد:</strong>
                            <span className={'flex items-center gap-1 font-medium text-sm text-darkPurple-60'}>
                                {item.destinationAmount.toLocaleString()}
                                <span className={'en'}>{item.destinationSymbol}</span>
                            </span>
                        </p>
                        <p className={'flex flex-col'}>
                            <strong className={'font-bold text-sm text-darkPurple-100'}>نوع تراکنش:</strong>
                            <span className={'font-medium text-sm text-darkPurple-60'}>
                                {item.orderTransactionType ? item.orderTransactionType.title : ''}
                            </span>
                        </p>
                    </div>
                    <div className="flex items-center justify-between">
                        <p className="flex flex-col">
                            <strong className="font-bold text-sm text-darkPurple-100">زمان:</strong>
                            <span className="font-medium text-sm text-darkPurple-60">
                                {moment.utc(item?.createdOn).locale('fa').local().format('jD jMMMM jYYYY - HH:mm')}
                            </span>
                        </p>
                        <Button
                            type="default"
                            size="small"
                            className="!h-8"
                            onClick={() => handleOrderTransactionDetail(item)}
                        >
                            جزئیات
                        </Button>
                    </div>
                </div>
            </li>
        );
    }

    function renderTransactionItem(item: Transaction) {
        return (
            <li className="p-4 rounded-[8px] bg-white border border-primary-20" key={item.trackingCode}>
                <div className="flex items-center justify-between">
                    <div className={'flex items-center gap-2'}>
                        <CoinIcon symbol={item.market.symbol} className="w-8 h-8" />
                        <span className={'text-sm font-bold'}>{item.market.name}</span>
                        <span className={'text-xs font-medium en'}>{item.market.symbol?.toUpperCase()}</span>
                    </div>
                    {item.state && (
                        <Badge
                            title={item.state.title}
                            type={getTransactionBadgeType(item.state.id) as 'success' | 'warning' | 'danger' | 'info'}
                        />
                    )}
                </div>
                <Divider className={'my-3'} />
                <div className={'flex justify-between'}>
                    <p className={'flex flex-col'}>
                        <strong className={'font-bold text-sm text-darkPurple-100'}>مقدار ارز:</strong>
                        <span className={'flex items-center gap-1 font-medium text-sm text-darkPurple-60'}>
                            {item.amount.toLocaleString()}
                            <span className={'en'}>{item.market.symbol}</span>
                        </span>
                    </p>
                    <p className={'flex flex-col'}>
                        <strong className={'font-bold text-sm text-darkPurple-100'}>قیمت واحد:</strong>
                        <span className={'font-medium text-sm text-darkPurple-60'}>
                            {item.onPrice.toLocaleString()} تومان
                        </span>
                    </p>
                    <p className={'flex flex-col'}>
                        <strong className={'font-bold text-sm text-darkPurple-100'}>نوع تراکنش:</strong>
                        <span className={'font-medium text-sm text-darkPurple-60'}>
                            {item.type ? item.type.title : ''}
                        </span>
                    </p>
                </div>
                <div className={'flex justify-between mt-2'}>
                    <Button
                        type={'default'}
                        className={'!h-8 justify-self-end'}
                        onClick={() => handleTransactionDetail(item)}
                    >
                        جزئیات
                    </Button>
                </div>
            </li>
        );
    }

    useEffect(() => {
        fetchData(page, pageSize);
    }, [
        page,
        pageSize,
        selectedOrderTransactionState,
        selectedOrderTransactionType,
        selectedTransactionState,
        selectedTransactionType,
        selectedReportType,
    ]);

    useEffect(() => {
        getTransactionTypes(setFiltersLoading, setTransactionTypes);
        getTransactionStates(setFiltersLoading, setTransactionStates);
        getOrderTransactionStates(setFiltersLoading, setOrderTransactionStates);
        getOrderTransactionTypes(setFiltersLoading, setOrderTransactionTypes);
    }, []);

    return (
        <div className={`w-full h-full ${props.type === undefined ? 'p-6' : ''}`}>
            {selectedReportType === ReportType.OrderTransaction && selectedOrderTransaction && (
                <ReportDetailModal
                    open={detailModalOpen}
                    setOpen={setDetailModalOpen}
                    item={selectedOrderTransaction}
                />
            )}
            {selectedReportType === ReportType.Transaction && selectedTransaction && (
                <ReportDetailModal
                    open={detailModalOpen}
                    setOpen={setDetailModalOpen}
                    item={selectedTransaction as Transaction}
                />
            )}
            {props.type === undefined && (
                <>
                    <div className="flex gap-4 items-center overflow-x-scroll scrollbar-hidden max-lg:gap-2">
                        {reportTypes.map((reportType) => (
                            <Button
                                key={reportType.id}
                                size="small"
                                className={'!h-12'}
                                type={selectedReportType === reportType.reportType ? 'primary' : 'default'}
                                onClick={() => setSelectedReportType(reportType.reportType)}
                            >
                                {reportType.title}
                            </Button>
                        ))}
                    </div>
                    <Input
                        className={'mt-6 h-12 w-72 max-lg:w-full'}
                        size={'small'}
                        placeholder={'ارز مورد نظر خود را جستجو کنید'}
                        prefix={<CiSearch />}
                        value={keyword}
                        onChange={handleKeywordChange}
                    />
                </>
            )}

            <div
                className={`flex max-lg:flex-col overflow-hidden gap-4 justify-between items-center ${props.type ? '' : 'mt-6'} w-full bg-white h-14 px-4 max-lg:h-auto ${props.type ? 'border-t border-b border-primary-20' : 'border border-primary-20 rounded-t-[8px] max-lg:rounded-[8px]'}`}
            >
                <div
                    className={
                        'flex lg:min-w-fit lg:border-l lg:border-l-primary-20 items-center gap-4 text-sm font-medium relative h-full overflow-x-auto scrollbar-hidden max-lg:gap-4 max-lg:justify-start max-lg:w-full max-lg:border-b max-lg:border-primary-20'
                    }
                >
                    {(selectedReportType === ReportType.OrderTransaction
                        ? orderTransactionTypes
                        : transactionTypes
                    )?.map((type) => (
                        <div
                            key={type.id}
                            className="relative clickable h-full flex items-center whitespace-nowrap"
                            onClick={() => {
                                if (selectedReportType === ReportType.OrderTransaction) {
                                    setSelectedOrderTransactionType(
                                        selectedOrderTransactionType?.id === type.id ? null : type,
                                    );
                                } else {
                                    setSelectedTransactionType(selectedTransactionType?.id === type.id ? null : type);
                                }
                            }}
                        >
                            <span
                                className={`py-2 px-4 transition duration-300 ease-in-out ${(selectedReportType === ReportType.OrderTransaction ? selectedOrderTransactionType : selectedTransactionType)?.id === type.id ? 'text-black-100' : 'text-black-60'}`}
                            >
                                {type.title}
                            </span>
                            {(selectedReportType === ReportType.OrderTransaction
                                ? selectedOrderTransactionType
                                : selectedTransactionType
                            )?.id === type.id && (
                                <div className="absolute left-0 right-0 bottom-0 h-1 bg-primary-100 rounded-t-[2.5px]" />
                            )}
                        </div>
                    ))}
                </div>
                <div
                    className={
                        'flex items-center justify-start gap-4 text-sm font-medium relative h-full overflow-x-scroll scrollbar-hidden max-lg:gap-4 max-lg:justify-start max-lg:w-full max-lg: whitespace-nowrap'
                    }
                >
                    {(selectedReportType === ReportType.OrderTransaction
                        ? orderTransactionStates
                        : transactionStates
                    )?.map((state) => (
                        <div
                            key={state.id}
                            className="relative clickable h-full flex items-center whitespace-nowrap"
                            onClick={() => {
                                if (selectedReportType === ReportType.OrderTransaction) {
                                    setSelectedOrderTransactionState(
                                        selectedOrderTransactionState?.id === state.id ? null : state,
                                    );
                                } else {
                                    setSelectedTransactionState(
                                        selectedTransactionState?.id === state.id ? null : state,
                                    );
                                }
                            }}
                        >
                            <span
                                className={`py-2 px-4 transition duration-300 ease-in-out ${(selectedReportType === ReportType.OrderTransaction ? selectedOrderTransactionState : selectedTransactionState)?.id === state.id ? 'text-black-100' : 'text-black-60'}`}
                            >
                                {state.title}
                            </span>
                            {(selectedReportType === ReportType.OrderTransaction
                                ? selectedOrderTransactionState
                                : selectedTransactionState
                            )?.id === state.id && (
                                <div className="absolute left-0 right-0 bottom-0 h-1 bg-primary-100 rounded-t-[2.5px]" />
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <Table
                className={'border-t-0 rounded-t-0 max-lg:hidden'}
                columns={
                    selectedReportType === ReportType.OrderTransaction ? orderTransactionColumns : transactionColumns
                }
                dataSource={
                    selectedReportType === ReportType.OrderTransaction ? orderTransactions?.items : transactions?.items
                }
                loading={loading}
                rowKey="id"
                pagination={false}
            />
            <div className="lg:hidden">
                <Spin className={'lg:hidden'} spinning={loading}>
                    <ul className="flex flex-col mt-4 gap-4 lg:hidden min-h-[500px] bg-background">
                        {selectedReportType === ReportType.OrderTransaction
                            ? orderTransactions?.items.map((item) => renderOrderTransactionItem(item))
                            : transactions?.items.map((item) => renderTransactionItem(item))}
                    </ul>
                </Spin>
            </div>

            <ConfigProvider locale={fa_IR}>
                <AntPagination
                    current={page}
                    pageSize={pageSize}
                    total={
                        selectedReportType === ReportType.OrderTransaction
                            ? orderTransactions?.pagination.totalCount
                            : transactions?.pagination.totalCount
                    }
                    onChange={handlePageChange}
                    className="mt-4 justify-center"
                    style={{ direction: 'ltr' }}
                    showSizeChanger={true}
                    onShowSizeChange={(current, size) => handlePageChange(current, size)}
                />
            </ConfigProvider>
        </div>
    );
};

export default React.forwardRef(Reports);
